import { elementSyncUtils } from "../utils/SyncUtils";
import { gatherProps } from "../utils/Merge/MergeUtils.mjs";
import { initFormatter } from "../utils/PropertyFormatters";

const isHoverProp = (propName) => propName.startsWith("hover.");
const getBaseStatePropName = (propName) => propName.substring(6);

/**
 * Hook for managing synchronized styles across layouts with support for link styles and hover properties
 * @param {Object} options - The options object
 * @param {string} options.parentType - Parent type in the theme hierarchy (e.g., 'buttons', 'styles', 'colors')
 * @param {string} options.childType - Child type in the theme hierarchy (e.g., 'heading', 'paragraph', 'primary', 'secondary')
 * @param {import('immutable').Map} options.styles - The style object from the theme to be synchronized
 * @param {string} options.selectedLayout - The currently selected layout ('desktop' or 'mobile')
 * @param {Function} options.onChange - Callback function for style changes
 * @param {string[]} options.propKeys - Array of property keys to gather from the styles
 * @param {Object} [options.linkConfig] - Optional configuration for link styles
 * @param {string[]} options.linkConfig.linkStyleProps - Array of link style properties to gather
 * @param {string} options.linkConfig.textColorProp - Property name for text color (e.g., 'c')
 * @param {string} options.linkConfig.hoverColorProp - Property name for hover color (e.g., 'hC')
 * @param {string} options.linkConfig.linkProp - Property name for link styles (e.g., 'link')
 * @param {Object} [options.hoverConfig] - Optional configuration for hover styles
 * @param {string[]} options.hoverConfig.hoverStyleProps - Array of hover style properties to gather
 * @param {boolean} [options.collectSubProps=false] - Whether to collect sub-properties when gathering props
 * @param {Object} [options.dynamicChildProps] - Optional map of parent props to their child props arrays
 * @returns {Object} Object containing style sync utilities (isSynced, toggleSyncedProp),
 *                   merged props (mergedSyncProps, mergedLinkSyncProps, mergedHoverSyncProps),
 *                   and helper functions (onFormatChange, prepChange, prepLinkChange, prepHoverChange)
 */
const useStyleSync = ({
  parentType = "styles",
  childType,
  styles,
  selectedLayout,
  onChange = () => {},
  linkConfig = null,
  propKeys,
  collectSubProps = true,
  dynamicChildProps = {},
}) => {
  const syncUtils = elementSyncUtils(
    styles,
    onChange,
    selectedLayout,
    "", // nestedPropPath
    "", // dynamicPropPrefix
    undefined, // inheritFrom
    dynamicChildProps,
  );

  const { isSynced, toggleSyncedProp, getPropName: baseGetPropName } = syncUtils;

  const getPropName = (propName) => `${parentType}.${childType}.${baseGetPropName(propName)}`;

  const mergedSyncProps = gatherProps(styles, selectedLayout, propKeys, collectSubProps);

  const { onChange: onFormatChange } = initFormatter(onChange, getPropName);

  const prepChange = (prop, getterPropName) => {
    const finalProp = getterPropName ?? prop;
    let originalValue = mergedSyncProps.getIn(finalProp.split("."));

    if (originalValue === undefined && isHoverProp(finalProp)) {
      const basePropName = getBaseStatePropName(finalProp);
      const basePropValue = mergedSyncProps.getIn(basePropName.split("."));
      originalValue = basePropValue;
    }

    return [prop, originalValue];
  };

  // sync
  const pathPrefix = `${parentType}.${childType}.`;

  const addToggle = (propName, mergeValueOverride, syncOverride) => ({
    layout: selectedLayout,
    sync: syncOverride !== undefined ? syncOverride : isSynced(propName),
    toggleSync: () => toggleSyncedProp(propName, pathPrefix, mergeValueOverride),
  });

  // link
  const mergedLinkSyncProps = linkConfig
    ? mergedSyncProps
        .set(linkConfig.hoverColorProp, `${mergedSyncProps.get(linkConfig.textColorProp)}:80`)
        .merge(
          gatherProps(styles, selectedLayout, linkConfig.linkStyleProps).get(linkConfig.linkProp)
        )
    : null;

  const prepLinkChange = (prop, fallbackProp) => [
    prop,
    mergedLinkSyncProps?.get(fallbackProp ?? prop) ?? mergedSyncProps.get(fallbackProp ?? prop),
  ];

  return {
    addToggle,
    getPropName,
    isSynced,
    mergedLinkSyncProps,
    mergedSyncProps,
    onFormatChange,
    prepChange,
    prepLinkChange,
    toggleSyncedProp,
  };
};

export default useStyleSync;

/**
 * Simplified version of useStyleSync for basic parent-child prop relationships
 * @param {Object} config - Basic configuration
 * @param {import('immutable').Map} config.styles - The style object to sync
 * @param {string} config.selectedLayout - Current layout
 * @param {Function} config.onChange - Change handler
 * @param {string[]} config.propKeys - Props to gather
 * @param {string} config.parentProp - The parent prop name
 * @param {string[]} config.childProps - Child props that sync with parent
 * @returns {Object} Sync utilities
 */
export const useSimpleStyleSync = ({
  styles,
  selectedLayout,
  onChange,
  propKeys,
  parentProp,
  childProps,
}) =>
  useStyleSync({
    styles,
    selectedLayout,
    onChange,
    propKeys,
    dynamicChildProps: { [parentProp]: childProps },
  });
